import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  SkipForward,
  TrendingUp,
  XCircle,
  Zap,
} from 'lucide-react';
import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Badge } from '../components/Badge';
import { type Column, DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { StatCard } from '../components/StatCard';
import {
  type AdminWorkflowNormalizedStatus,
  useAdminWorkflowMetrics,
  useAdminWorkflows,
  type WorkflowRow,
} from '../hooks/useAdminData';

const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(225, 20%, 16%)',
  border: '1px solid hsl(225, 15%, 25%)',
  borderRadius: '8px',
  color: 'hsl(0, 0%, 95%)',
};

const GRID_STROKE = 'hsl(225, 15%, 18%)';
const AXIS_STROKE = 'hsl(0, 0%, 45%)';
const PRIMARY_COLOR = 'hsl(184, 92%, 56%)';
const SUCCESS_COLOR = 'hsl(142, 71%, 45%)';
const DANGER_COLOR = 'hsl(0, 84%, 60%)';
const WARNING_COLOR = 'hsl(38, 92%, 50%)';
const INFO_COLOR = 'hsl(217, 91%, 60%)';

const STATUS_CONFIG: Record<
  AdminWorkflowNormalizedStatus,
  { color: string; variant: 'success' | 'danger' | 'warning' | 'info' | 'default' }
> = {
  succeeded: { color: SUCCESS_COLOR, variant: 'success' },
  failed: { color: DANGER_COLOR, variant: 'danger' },
  running: { color: INFO_COLOR, variant: 'info' },
  partial: { color: WARNING_COLOR, variant: 'warning' },
  cancelled: { color: AXIS_STROKE, variant: 'default' },
};

const SOURCE_LABELS: Record<WorkflowRow['source'], string> = {
  enrichment: 'Enrichment',
  voice: 'Voice',
};

interface EnrichmentStageInfo {
  stage: string;
  status: 'success' | 'failed' | 'skipped' | 'not_reached';
  reasonCode?: string;
}

const STAGE_LABELS: Record<string, string> = {
  profile: 'Profile',
  posts: 'Posts',
  comments: 'Comments',
  analysis: 'Analysis',
  company: 'Company',
  company_posts: 'Company posts',
};

const STAGE_STATUS_CONFIG: Record<
  EnrichmentStageInfo['status'],
  { icon: typeof CheckCircle2; className: string; label: string }
> = {
  success: { icon: CheckCircle2, className: 'text-success', label: 'ok' },
  failed: { icon: XCircle, className: 'text-danger', label: 'failed' },
  skipped: { icon: SkipForward, className: 'text-warning', label: 'skipped' },
  not_reached: { icon: Circle, className: 'text-text-muted', label: 'not reached' },
};

function parseStages(metadata: Record<string, unknown>): EnrichmentStageInfo[] | null {
  const stages = metadata.stages;
  return Array.isArray(stages) ? (stages as EnrichmentStageInfo[]) : null;
}

function parseActorErrors(metadata: Record<string, unknown>): Record<string, string> | null {
  const actorErrors = metadata.actorErrors;
  return actorErrors && typeof actorErrors === 'object'
    ? (actorErrors as Record<string, string>)
    : null;
}

function EnrichmentDetailSections({ metadata }: { metadata: Record<string, unknown> }) {
  const stages = parseStages(metadata);
  const actorErrors = parseActorErrors(metadata);
  const analysisStage = stages?.find((s) => s.stage === 'analysis');
  const analysisOk = analysisStage?.status === 'success';
  const analysisLabel = analysisStage
    ? (STAGE_STATUS_CONFIG[analysisStage.status]?.label ?? analysisStage.status)
    : '';

  return (
    <>
      {stages && (
        <>
          {analysisStage && (
            <div
              className={`rounded-lg border px-3 py-2 text-sm flex items-center gap-2 ${
                analysisOk
                  ? 'border-success/30 bg-success/10 text-success'
                  : 'border-warning/30 bg-warning/10 text-warning'
              }`}
            >
              {analysisOk ? (
                <CheckCircle2 size={14} aria-hidden="true" />
              ) : (
                <AlertTriangle size={14} aria-hidden="true" />
              )}
              <span>{analysisOk ? 'AI analysis completed' : `AI analysis ${analysisLabel}`}</span>
            </div>
          )}

          <div className="space-y-2">
            <span className="text-xs text-text-muted">Stage timeline</span>
            <ul className="space-y-1">
              {stages.map((s) => {
                const config = STAGE_STATUS_CONFIG[s.status] ?? STAGE_STATUS_CONFIG.not_reached;
                const Icon = config.icon;
                return (
                  <li key={s.stage} className="flex items-center gap-2 text-sm">
                    <Icon size={14} className={config.className} aria-hidden="true" />
                    <span className="text-text">{STAGE_LABELS[s.stage] ?? s.stage}</span>
                    <span className={`text-xs ${config.className}`}>{config.label}</span>
                    {s.reasonCode && (
                      <span className="text-xs text-text-muted">({s.reasonCode})</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}

      {actorErrors && Object.keys(actorErrors).length > 0 && (
        <div className="space-y-1">
          <span className="text-xs text-text-muted">Actor errors</span>
          <ul className="space-y-1">
            {Object.entries(actorErrors).map(([actor, message]) => (
              <li key={actor} className="text-xs text-danger">
                <span className="font-medium">{actor}:</span> {message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

const DAYS_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

const SOURCE_OPTIONS: Array<{ value: '' | WorkflowRow['source']; label: string }> = [
  { value: '', label: 'All sources' },
  { value: 'enrichment', label: 'Enrichment' },
  { value: 'voice', label: 'Voice' },
];

const STATUS_OPTIONS: Array<{ value: '' | AdminWorkflowNormalizedStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'running', label: 'Running' },
  { value: 'partial', label: 'Partial' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PAGE_SIZE = 20;

function formatWorkflowType(type: string) {
  return type.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDuration(ms: number | null) {
  if (ms == null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

function ChartSkeleton() {
  return (
    <div className="h-64 flex items-center justify-center">
      <div className="text-text-muted">Loading...</div>
    </div>
  );
}

export function Workflows() {
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(1);
  const [sourceFilter, setSourceFilter] = useState<'' | WorkflowRow['source']>('');
  const [statusFilter, setStatusFilter] = useState<'' | AdminWorkflowNormalizedStatus>('');
  const [selectedRow, setSelectedRow] = useState<WorkflowRow | null>(null);

  const {
    data: metrics,
    isLoading: metricsLoading,
    isError: metricsIsError,
  } = useAdminWorkflowMetrics({ days });
  const {
    data: workflows,
    isLoading: workflowsLoading,
    isError: workflowsIsError,
  } = useAdminWorkflows({
    page,
    limit: PAGE_SIZE,
    ...(sourceFilter && { source: sourceFilter }),
    ...(statusFilter && { status: statusFilter }),
  });

  const hasError = metricsIsError || workflowsIsError;

  const summary = metrics?.summary;

  const statusPieData = summary
    ? [
        { name: 'Succeeded', value: summary.succeeded, color: SUCCESS_COLOR },
        { name: 'Failed', value: summary.failed, color: DANGER_COLOR },
        { name: 'Running', value: summary.running, color: INFO_COLOR },
        { name: 'Partial', value: summary.partial, color: WARNING_COLOR },
        { name: 'Cancelled', value: summary.cancelled, color: AXIS_STROKE },
      ].filter((s) => s.value > 0)
    : [];

  const sourceBarData =
    metrics?.bySource.map((s) => ({
      name: SOURCE_LABELS[s.source],
      source: s.source,
      count: s.count,
    })) ?? [];

  const columns: Column<WorkflowRow>[] = [
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span title={`raw: ${row.rawStatus}`}>
          <Badge variant={STATUS_CONFIG[row.status]?.variant ?? 'default'}>{row.status}</Badge>
        </span>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      render: (row) => <span className="text-text-secondary">{SOURCE_LABELS[row.source]}</span>,
    },
    {
      key: 'workflowType',
      header: 'Type',
      render: (row) => (
        <span className="text-text font-medium">{formatWorkflowType(row.workflowType)}</span>
      ),
    },
    {
      key: 'organizationName',
      header: 'Organization',
      render: (row) => <span className="text-text-secondary">{row.organizationName}</span>,
    },
    {
      key: 'startedAt',
      header: 'Started',
      render: (row) => (
        <span className="text-text-muted text-xs whitespace-nowrap">
          {formatDistanceToNow(new Date(row.startedAt), { addSuffix: true })}
        </span>
      ),
    },
    {
      key: 'durationMs',
      header: 'Duration',
      render: (row) => <span className="text-text-secondary">{formatDuration(row.durationMs)}</span>,
    },
    {
      key: 'itemsAffected',
      header: 'Items',
      render: (row) => <span className="text-text-secondary">{row.itemsAffected}</span>,
    },
    {
      key: 'errorMessage',
      header: 'Error',
      render: (row) =>
        row.errorMessage ? (
          <span className="text-danger truncate max-w-[160px] inline-block">
            {row.errorMessage}
          </span>
        ) : (
          '-'
        ),
    },
  ];

  if (hasError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Workflows</h1>
          <p className="text-sm text-text-muted mt-1">
            Enrichment and voice workflow executions across all organizations
          </p>
        </div>
        <div className="bg-surface rounded-xl border border-border px-5 py-8 text-center text-sm">
          <p className="text-danger font-medium">Failed to load workflow data</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-3 text-text-secondary underline hover:text-text transition-colors text-xs"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Workflows</h1>
          <p className="text-sm text-text-muted mt-1">
            Enrichment and voice workflow executions across all organizations
          </p>
        </div>
        <div className="flex items-center gap-3">
          {summary && summary.running > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-info/10 border border-info/30 rounded-lg">
              <Loader2 size={14} className="text-info animate-spin" />
              <span className="text-sm text-info font-medium">{summary.running} running</span>
            </div>
          )}
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
          >
            {DAYS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Runs"
          value={summary?.total ?? 0}
          icon={Zap}
          loading={metricsLoading}
        />
        <StatCard
          label="Success Rate"
          value={summary ? `${summary.successRate}%` : '0%'}
          icon={TrendingUp}
          loading={metricsLoading}
        />
        <StatCard
          label="Failed"
          value={summary?.failed ?? 0}
          icon={XCircle}
          loading={metricsLoading}
        />
        <StatCard
          label="Avg Duration"
          value={formatDuration(summary?.avgDurationMs ?? 0)}
          icon={Clock}
          loading={metricsLoading}
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Status Distribution</h2>
          {metricsLoading ? (
            <ChartSkeleton />
          ) : statusPieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={95}
                  dataKey="value"
                  nameKey="name"
                  paddingAngle={2}
                  label={({ name, value }) => `${name} (${value})`}
                >
                  {statusPieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>

        <div className="bg-surface rounded-xl border border-border p-5">
          <h2 className="text-sm font-medium text-text-secondary mb-4">Runs by Source</h2>
          {metricsLoading ? (
            <ChartSkeleton />
          ) : sourceBarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={sourceBarData}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} />
                <XAxis dataKey="name" stroke={AXIS_STROKE} fontSize={12} />
                <YAxis stroke={AXIS_STROKE} fontSize={12} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" name="Runs" radius={[4, 4, 0, 0]} fill={PRIMARY_COLOR} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-text-muted">No data</div>
          )}
        </div>
      </div>

      {/* Filters + table */}
      <div className="bg-surface rounded-xl border border-border">
        <div className="px-5 py-4 border-b border-border flex flex-wrap gap-2">
          <select
            value={sourceFilter}
            aria-label="Filter by source"
            onChange={(e) => {
              setSourceFilter(e.target.value as '' | WorkflowRow['source']);
              setPage(1);
            }}
            className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary"
          >
            {SOURCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            aria-label="Filter by status"
            onChange={(e) => {
              setStatusFilter(e.target.value as '' | AdminWorkflowNormalizedStatus);
              setPage(1);
            }}
            className="px-3 py-1.5 bg-background border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <DataTable
          columns={columns}
          data={(workflows?.data ?? []) as (WorkflowRow & Record<string, unknown>)[]}
          total={workflows?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          onRowClick={(row) => setSelectedRow(row)}
          loading={workflowsLoading}
        />
      </div>

      <Modal
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        title={
          selectedRow
            ? `${formatWorkflowType(selectedRow.workflowType)} — ${selectedRow.organizationName}`
            : 'Workflow detail'
        }
      >
        {selectedRow && (
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Badge variant={STATUS_CONFIG[selectedRow.status]?.variant ?? 'default'}>
                {selectedRow.status}
              </Badge>
              <span className="text-xs text-text-muted">raw: {selectedRow.rawStatus}</span>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
              <div>
                <span className="text-xs text-text-muted">Started</span>
                <div className="text-text">{new Date(selectedRow.startedAt).toLocaleString()}</div>
              </div>
              <div>
                <span className="text-xs text-text-muted">Duration</span>
                <div className="text-text">{formatDuration(selectedRow.durationMs)}</div>
              </div>
            </div>

            {selectedRow.errorMessage && (
              <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-danger">
                {selectedRow.errorMessage}
              </div>
            )}

            {selectedRow.source === 'enrichment' ? (
              <>
                <div className="grid grid-cols-3 gap-x-4 gap-y-2">
                  <div>
                    <span className="text-xs text-text-muted">Leads created</span>
                    <div className="text-text">
                      {String(selectedRow.metadata.leadsCreated ?? 0)}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-text-muted">Leads updated</span>
                    <div className="text-text">
                      {String(selectedRow.metadata.leadsUpdated ?? 0)}
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-text-muted">Errors</span>
                    <div className="text-text">{String(selectedRow.metadata.errorCount ?? 0)}</div>
                  </div>
                </div>

                <EnrichmentDetailSections metadata={selectedRow.metadata} />
              </>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div>
                  <span className="text-xs text-text-muted">Cost (credits)</span>
                  <div className="text-text">{String(selectedRow.metadata.costCredits ?? '-')}</div>
                </div>
                <div>
                  <span className="text-xs text-text-muted">Audio duration</span>
                  <div className="text-text">
                    {String(selectedRow.metadata.audioDuration ?? '-')}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-text-muted">Delivery channel</span>
                  <div className="text-text">
                    {String(selectedRow.metadata.deliveryChannel ?? '-')}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-text-muted">Characters</span>
                  <div className="text-text">
                    {String(selectedRow.metadata.characterCount ?? '-')}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
